import { useState, useCallback } from "react";
import { API_BASE } from "../config/api";
import { httpRequest } from "../utils/httpClient";
import { authService } from "../services/auth";
import logger from '../utils/logger';

export function useSimplifyGames() {
  // Game selection state
  const [selectedGames, setSelectedGames] = useState([]);
  const [gameSearchQuery, setGameSearchQuery] = useState("");
  const [gameSearchResults, setGameSearchResults] = useState([]);
  const [isGameSearchLoading, setIsGameSearchLoading] = useState(false);
  const [showGameDropdown, setShowGameDropdown] = useState(false);

  // Results state
  const [recommendations, setRecommendations] = useState([]);
  const [disqualifiedGames, setDisqualifiedGames] = useState([]);

  // Collection loading state
  const [loadingCollection, setLoadingCollection] = useState(false);

  // Game preview state
  const [previewGame, setPreviewGame] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const selectGame = useCallback((game) => {
    setSelectedGames(prev => {
      if (prev.find(g => g.id === game.id)) {
        return prev;
      }
      return [...prev, game];
    });
    setGameSearchQuery("");
    setShowGameDropdown(false);
  }, []);

  const removeGame = useCallback((gameId) => {
    setSelectedGames(prev => prev.filter(g => g.id !== gameId));
  }, []);

  const clearGames = useCallback(() => {
    setSelectedGames([]);
  }, []);

  const clearRecommendations = useCallback(() => {
    setRecommendations([]);
    setDisqualifiedGames([]);
  }, []);

  const resetGameSearch = useCallback(() => {
    setGameSearchQuery("");
    setGameSearchResults([]);
    setShowGameDropdown(false);
  }, []);

  // Find games from user collection via backend API.
  // The backend fetches owned games directly; no collection array needed here.
  const findInCollection = useCallback(async (preferences) => {

    let loadingTimer = null;
    try {
      // Start loading timer (show loading only if it takes more than 1 second)
      loadingTimer = setTimeout(() => {
        setLoadingCollection(true);
      }, 1000);

      // Call backend API for find-in-collection
      // Map frontend preference keys to backend expected keys
      // TODO: Align preference key naming between frontend useSimplifyPreferences and backend simplify.py
      const mappedPreferences = {
        players: preferences.players,
        timePref: preferences.time,
        energyPref: preferences.energy,
        feelPref: preferences.feel,
        familiarityPref: preferences.familiarity,
      };

      const response = await httpRequest(
        `${API_BASE}/simplify/find-in-collection`,
        {
          method: "POST",
          headers: {
            ...authService.getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            preferences: mappedPreferences,
            exclude_days: 30,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      // Clear loading timer and state
      if (loadingTimer) {
        clearTimeout(loadingTimer);
      }
      setLoadingCollection(false);

      // API returns 10 games with recency penalty applied
      // disqualified is always empty per backend contract
      return {
        recommendations: data.recommendations || [],
        disqualified: [] // Always empty per requirement
      };
    } catch (err) {
      logger.error("Error using collection API:", err);

      if (loadingTimer) {
        clearTimeout(loadingTimer);
      }
      setLoadingCollection(false);

      throw err; // Clean break: no local fallback
    }
  }, []);

  // Get recommendations via backend batch scoring (clean break from local rankGames)
  const getRecommendations = useCallback(async (gameIds, preferences) => {
    if (!gameIds || gameIds.length === 0) {
      return { recommendations: [], disqualified: [] };
    }

    // Map frontend preference keys to backend expected keys
    const mappedPreferences = {
      players: preferences.players,
      timePref: preferences.time,
      energyPref: preferences.energy,
      feelPref: preferences.feel,
      familiarityPref: preferences.familiarity,
    };

    const response = await httpRequest(
      `${API_BASE}/simplify/batch-score`,
      {
        method: "POST",
        headers: {
          ...authService.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          game_ids: gameIds,
          preferences: mappedPreferences,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Batch score API error: ${response.status}`);
    }

    const data = await response.json();

    // Transform backend response to expected format
    // Backend returns scored_games sorted by score desc
    const scoredGames = data.scored_games || [];

    // Disqualify gated games (player count mismatch, score === -999)
    const recommendations = scoredGames.filter(g => g.score !== -999);
    const disqualified = scoredGames.filter(g => g.score === -999);

    return {
      recommendations,
      disqualified
    };
  }, []);

  return {
    // Selection
    selectedGames,
    setSelectedGames,
    selectGame,
    removeGame,
    clearGames,
    // Search
    gameSearchQuery,
    setGameSearchQuery,
    gameSearchResults,
    setGameSearchResults,
    isGameSearchLoading,
    setIsGameSearchLoading,
    showGameDropdown,
    setShowGameDropdown,
    resetGameSearch,
    // Results
    recommendations,
    setRecommendations,
    disqualifiedGames,
    setDisqualifiedGames,
    clearRecommendations,
    // Preview
    previewGame,
    setPreviewGame,
    loadingPreview,
    setLoadingPreview,
    // Collection
    loadingCollection,
    findInCollection,
    // Batch scoring
    getRecommendations
  };
}
